import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react';
import React, {useId, useState} from 'react';

import classNames from 'classnames';
import CSSModules from 'react-css-modules';

import {
  CrossIcon,
  EmailIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  KeyIcon,
  SearchIcon,
} from '../../icons';
import {renderValidationText} from '../formUtils';
import Label from '../Label';

import styles from './styles.module.scss';

type ConditionalLabelProps =
  | {
      label: string;
      labelledBy?: never;
      htmlId?: string;
    }
  | {
      label?: never;
      labelledBy: string;
      htmlId?: string;
    }
  | {
      label?: never;
      labelledBy?: never;
      htmlId: string;
    };

type ConditionalActionProps =
  | {
      showClearButton?: true;
      clearButtonLabel: string;
      clearButtonIcon?: JSX.Element;
    }
  | {
      showClearButton?: false;
      clearButtonLabel?: string;
      clearButtonIcon?: never;
    };

type NumberProps = {min?: number; max?: number; step?: number};

type ConditionalInputTypeProps =
  | {
      type: 'password';
      showVisibilityToggle?: boolean;
      showText?: string;
      hideText?: string;
    }
  | {
      type: 'number-increment';
      showVisibilityToggle?: never;
      showText?: never;
      hideText?: never;
    }
  | {
      type: 'email' | 'text' | 'search' | 'prompt' | 'number' | 'date';
      numberProps?: never;
      showVisibilityToggle?: never;
      showText?: never;
      hideText?: never;
    };

type CommonInputProps = {
  size?: 'default' | 'small';
  rows?: number;
  value?: string | number;

  // Text strings
  optionalText?: string;
  countText?: string;
  errorText?: string;
  successText?: string;
  placeholder?: string;

  // Events
  onChangeValue?: (e: string) => void;
  onClearInput?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onEnterKey?: () => void;
  onFocus?: (
    target: EventTarget & (HTMLInputElement | HTMLTextAreaElement)
  ) => void;
  onBlur?: () => void;

  // States
  isDisabled?: boolean;
  isInvalid?: boolean;
  isCountWarning?: boolean;

  // Display / functional options
  autoFocus?: boolean;
  ignorePrefilled?: boolean;
  includeBottomMargin?: boolean;
  isLabelVisible?: boolean;
  removeSpellcheck?: boolean;
  showTypeIcon?: boolean;

  // Validation setup
  validationStyle?: 'default' | 'in-label';
  reserveValidationSpace?: boolean;

  // Number-specific props
  numberProps?: NumberProps;

  // Testing
  dataAutomation?: string;
  clearInputDataAutomation?: string;
  readyStatusDataAutomation?: string;

  /** @deprecated - shows the circle validation */
  useLegacyIcons?: boolean;
};

export type InputProps = CommonInputProps &
  ConditionalLabelProps &
  ConditionalActionProps &
  ConditionalInputTypeProps;

const getPasswordVisible = (visible: boolean) =>
  visible ? 'text' : 'password';

const getInputType = (type: string, isPasswordVisible: boolean) => {
  if (type === 'password') {
    return getPasswordVisible(isPasswordVisible);
  } else if (['search', 'prompt', 'number'].includes(type)) {
    return 'text';
  } else {
    return type;
  }
};

const getInputSpecificProps = (type: string, numberProps?: NumberProps) => {
  switch (type) {
    case 'email':
    case 'search':
    case 'tel':
    case 'url':
      return {inputMode: type};
    case 'number-increment':
      return {...numberProps, inputMode: 'numeric' as const};
    case 'number':
      return {
        pattern: '[0-9]*',
        inputMode: 'numeric' as const,
      };
    default:
      return {};
  }
};

const getTypeIcon = (type: string, show: boolean, size: string) => {
  if (!show || size !== 'default') {
    return false;
  }
  switch (type) {
    case 'email':
      return <EmailIcon />;
    case 'search':
      return <SearchIcon />;
    case 'password':
      return <KeyIcon />;
    default:
      return false;
  }
};

const renderTypeIcon = (
  showTypeIcon: boolean,
  size: 'default' | 'small',
  type: string
) =>
  showTypeIcon &&
  size !== 'small' &&
  ['email', 'search', 'password'].includes(type);

const getVisibilityIcon = (isVisible: boolean) =>
  isVisible ? <EyeClosedIcon /> : <EyeOpenIcon />;

const getVisibilityText = (
  isVisible: boolean,
  showText: string | undefined,
  hideText: string | undefined
) => {
  const showTextValue = showText || '';
  const hideTextValue = hideText || '';
  return isVisible ? hideTextValue : showTextValue;
};

const getIsPrefilled = (
  value: string | number | undefined,
  originalValue: string | number | undefined,
  ignorePrefilled: boolean
) => value === originalValue && originalValue !== '' && ignorePrefilled;

const getIsClearButtonShown = (
  isDisabled: boolean,
  showClearButton: boolean,
  rows: number
) => !isDisabled && showClearButton && rows === 1;

const Input = ({
  type = 'text',
  size = 'default',
  rows = 1,
  value,
  label,
  optionalText,
  countText,
  errorText,
  successText,
  placeholder,
  clearButtonLabel,
  showText,
  hideText,
  onChangeValue,
  onClearInput,
  onEnterKey,
  onFocus,
  onBlur,
  isDisabled = false,
  isInvalid,
  isCountWarning = false,
  autoFocus = false,
  ignorePrefilled = false,
  includeBottomMargin = true,
  isLabelVisible = true,
  removeSpellcheck = false,
  showClearButton = false,
  clearButtonIcon,
  showTypeIcon = false,
  showVisibilityToggle,
  validationStyle = 'default',
  reserveValidationSpace = true,
  numberProps,
  dataAutomation,
  clearInputDataAutomation,
  readyStatusDataAutomation,
  htmlId,
  labelledBy,
  useLegacyIcons,
}: InputProps) => {
  const fieldId = useId();
  const [originalValue] = useState(value);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const isPrefilled = getIsPrefilled(value, originalValue, ignorePrefilled);

  const isClearButtonShown = getIsClearButtonShown(
    isDisabled,
    showClearButton,
    rows
  );

  const toggleVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const getIdProp = () => (htmlId ? htmlId : fieldId);

  const handleClearButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClearInput) {
      onClearInput(e);
    }

    if (onChangeValue) {
      onChangeValue('');
    }
  };

  const handleOnChangeValue: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (onChangeValue) {
      onChangeValue(e.target.value);
    }
  };

  const handleKeyPress: KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (e.key === 'Enter' && onEnterKey) {
      onEnterKey();
    }
  };

  const handleFocus: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setIsFocused(true);

    if (onFocus) {
      onFocus(e.target);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);

    if (onBlur) {
      onBlur();
    }
  };

  const isSuccessTextShown = () =>
    !isInvalid &&
    !isPrefilled &&
    successText !== '' &&
    successText !== undefined &&
    value !== '';

  const isErrorTextShown = () =>
    !!isInvalid && !isPrefilled && errorText !== '' && errorText !== undefined;

  const labelElement = (text: string | undefined) => {
    if (text === undefined) {
      return false;
    }
    return (
      <Label
        isLabelVisible={isLabelVisible}
        htmlFor={fieldId}
        label={text}
        optionalText={optionalText}
        validationStyle={validationStyle === 'in-label' ? 'icon' : 'none'}
        isInvalid={isInvalid}
        showValidation={
          validationStyle === 'in-label' && (value !== '' || isInvalid)
        }
        useLegacyIcons={useLegacyIcons}
        readyStatusDataAutomation={readyStatusDataAutomation}
      />
    );
  };

  const inputElement = () => {
    const inputProps = {
      id: getIdProp(),
      rows: type === 'text' ? rows : 1,
      dir: 'auto',
      type: getInputType(type, isPasswordVisible),
      styleName: classNames('input', {
        'input-element': rows === 1,
        small: size === 'small',
        invalid: isErrorTextShown(),
        valid: isSuccessTextShown(),
        'type-icon': renderTypeIcon(showTypeIcon, size, type),
        action: isClearButtonShown || showVisibilityToggle,
      }),
      placeholder,
      onChange: handleOnChangeValue,
      onKeyDown: handleKeyPress,
      onFocus: handleFocus,
      onBlur: handleBlur,
      disabled: isDisabled,
      'data-automation': dataAutomation,
      'aria-invalid': isInvalid,
      'aria-describedby': getIdProp() + '-error',
      'aria-labelledby': labelledBy,
      value,
      autoFocus,
      spellCheck: !removeSpellcheck,
    };
    return rows === 1 ? (
      <input {...inputProps} {...getInputSpecificProps(type, numberProps)} />
    ) : (
      <textarea {...inputProps} {...getInputSpecificProps(type, numberProps)} />
    );
  };

  const clearButton = () => {
    if (!isClearButtonShown || size !== 'default' || type === 'password') {
      return false;
    }
    return (
      <button
        onClick={handleClearButton}
        data-automation={clearInputDataAutomation}
        type='button'
      >
        {clearButtonIcon ? clearButtonIcon : <CrossIcon />}
        <span className='visually-hidden'>{clearButtonLabel}</span>
      </button>
    );
  };

  const passwordToggleButton = () => (
    <button onClick={toggleVisibility}>
      {getVisibilityIcon(isPasswordVisible)}
      <span className='visually-hidden'>
        {getVisibilityText(isPasswordVisible, showText, hideText)}
      </span>
    </button>
  );

  return (
    <div
      styleName={classNames('input-wrapper', {
        bottomMargin: includeBottomMargin,
      })}
    >
      {labelElement(label)}
      <div styleName='input-inner-wrapper'>
        {getTypeIcon(type, showTypeIcon, size)}
        {inputElement()}
        {clearButton()}
        {showVisibilityToggle && type === 'password' && passwordToggleButton()}
      </div>
      <div
        styleName={classNames('secondary-text', {
          noMargin: !reserveValidationSpace,
          minValidationHeight: reserveValidationSpace,
        })}
      >
        {countText && isFocused && (
          <p
            styleName={classNames('character-count', {warning: isCountWarning})}
          >
            {countText}
          </p>
        )}
        {validationStyle === 'default' &&
          renderValidationText({
            errorText: errorText,
            showInvalidState: isErrorTextShown(),
            successText: successText,
            showValidState: isSuccessTextShown(),
            reserveValidationSpace: reserveValidationSpace,
            errorTextId: getIdProp() + '-error',
            isPrefilled: isPrefilled,
          })}
      </div>
    </div>
  );
};

export default CSSModules(Input, styles, {allowMultiple: true});
